import { resolveTargetPath as resolvePathForRole } from "./resolve-target-path";
import { beginApiAttempt, type ApiAttemptMeasurement, type ApiRequestObserver } from "./request-measurement";

declare global {
  interface Window {
    __anlRolePreview?: string | null;
  }
}

const previewRoleStorageKey = "anl-role-preview";
const localPreviewRoles = new Set([
  "PLANLAMA",
  "SATIN_ALMA",
  "SATIN_ALMA_MUDURU",
  "GENEL_MUDUR",
  "KALITE_UZMANI",
  "KALITE_MUDURU",
  "URETIM_OPERATORU",
  "VARDIYA_AMIRI",
  "URETIM_YONETICISI",
  "TEKNIK_MUDURU",
  "DEPO_GOREVLISI",
  "CEP_DEPO_SORUMLUSU",
]);

function localPreviewRoleFromUrl() {
  // A copied demo link must never select a role on the deployed application.
  if (!import.meta.env?.DEV || !["localhost", "127.0.0.1", "[::1]"].includes(window.location.hostname)) {
    return null;
  }
  const requestedRole = new URLSearchParams(window.location.search).get("demoRole");
  if (requestedRole === "SISTEM_YONETICISI") {
    try {
      window.sessionStorage.removeItem(previewRoleStorageKey);
    } catch {
      // Kısıtlı tarayıcı depolamasında değer yalnız bu yüklemede sıfırlanır.
    }
    window.__anlRolePreview = null;
    return null;
  }
  return requestedRole && localPreviewRoles.has(requestedRole) ? requestedRole : null;
}

function storedPreviewRole() {
  try {
    return window.sessionStorage.getItem(previewRoleStorageKey);
  } catch {
    return null;
  }
}

// A local role URL wins over a tab's previous choice, so each shared demo link
// opens the intended role in its own tab session. The API still requires the
// local test administrator before it accepts this role header.
let previewRole = localPreviewRoleFromUrl() ?? window.__anlRolePreview ?? storedPreviewRole();
if (previewRole) {
  try {
    window.sessionStorage.setItem(previewRoleStorageKey, previewRole);
  } catch {
    // Mevcut sayfadaki rol önizlemesi depolama olmadan çalışmaya devam eder.
  }
}
const responseCache = new Map<string, unknown>();

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly requestId?: string,
    readonly errorType?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

function resolveTargetPath(path: string) {
  return resolvePathForRole(path, previewRole);
}

function cacheKey(path: string) {
  return `${previewRole ?? "session"}:${resolveTargetPath(path)}`;
}

export function peekApiResponse<T>(path: string): T | undefined {
  return responseCache.get(cacheKey(path)) as T | undefined;
}

export function clearApiResponseCache() {
  responseCache.clear();
}

export function setRolePreview(role: string | null) {
  previewRole = role;
  window.__anlRolePreview = role;
  try {
    if (role) window.sessionStorage.setItem(previewRoleStorageKey, role);
    else window.sessionStorage.removeItem(previewRoleStorageKey);
  } catch {
    // Kısıtlı tarayıcı depolamasında önizleme yalnız mevcut sayfada çalışır.
  }
}

export function getRolePreview() {
  return previewRole;
}

export async function apiRequest<T>(
  path: string,
  options?: RequestInit,
  observeAttempt?: ApiRequestObserver,
): Promise<T> {
  const targetPath = resolveTargetPath(path);
  const headers = new Headers(options?.headers);
  if (previewRole) headers.set("x-demo-role", previewRole);
  if (!(options?.body instanceof FormData) && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }
  const method = (options?.method ?? "GET").toUpperCase();
  const maximumAttempts = method === "GET" || method === "HEAD" ? 2 : 1;
  let response: Response | null = null;
  let networkError: unknown;
  let measurement: ApiAttemptMeasurement | undefined;

  for (let attempt = 0; attempt < maximumAttempts; attempt += 1) {
    measurement = beginApiAttempt(attempt + 1, observeAttempt);
    const attemptHeaders = new Headers(headers);
    if (measurement) attemptHeaders.set("x-request-id", measurement.clientRequestId);
    try {
      response = await fetch(targetPath, {
        ...options,
        cache: previewRole ? "no-store" : options?.cache,
        credentials: "same-origin",
        headers: attemptHeaders,
      });
      measurement?.received(response);
      break;
    } catch (error) {
      networkError = error;
      measurement?.finish("FAILURE", "NETWORK_ERROR");
      if (options?.signal?.aborted) break;
      if (attempt + 1 < maximumAttempts) {
        await new Promise((resolve) => window.setTimeout(resolve, 250));
      }
    }
  }

  if (!response) {
    void networkError;
    throw new ApiError(
      "Sunucuya bağlanılamadı. Bağlantınızı kontrol edip yeniden deneyin.",
      "NETWORK_ERROR",
    );
  }

  try {
    if (!response.ok) {
      const body = (await (measurement ? measurement.json(response) : response.json()).catch(() => null)) as {
        message?: string;
        code?: string;
        requestId?: string;
        errorType?: string;
      } | null;
      throw new ApiError(
        body?.message ?? "İşlem tamamlanamadı.",
        body?.code ?? `HTTP_${response.status}`,
        body?.requestId ?? response.headers.get("x-request-id") ?? undefined,
        body?.errorType,
      );
    }

    if (response.status === 204) {
      if (method !== "GET" && method !== "HEAD") clearApiResponseCache();
      measurement?.finish("SUCCESS", null);
      return undefined as T;
    }

    if (!response.headers.get("content-type")?.includes("application/json")) {
      throw new ApiError("Sunucu bu işlem için geçerli yanıt vermedi. API güncellemesi veya oturum kontrolü gerekiyor; notunuz korunuyor.", "INVALID_API_RESPONSE");
    }
    const data = (await (measurement ? measurement.json(response) : response.json()).catch(() => { throw new ApiError("Sunucu yanıtı okunamadı. Yeniden deneyin.", "INVALID_API_RESPONSE"); })) as T;
    if (
      (method === "GET" || method === "HEAD") &&
      options?.cache !== "no-store"
    ) {
      responseCache.set(cacheKey(path), data);
    } else if (method !== "GET" && method !== "HEAD") {
      clearApiResponseCache();
    }
    measurement?.finish("SUCCESS", null);
    return data;
  } catch (error) {
    measurement?.finish("FAILURE", error instanceof ApiError ? error.code : "INVALID_API_RESPONSE");
    throw error;
  }
}

export async function apiBlobRequest(path: string): Promise<Blob> {
  const headers = new Headers();
  if (previewRole) headers.set("x-demo-role", previewRole);
  const response = await fetch(resolveTargetPath(path), {
    cache: previewRole ? "no-store" : "default",
    credentials: "same-origin",
    headers,
  });
  if (!response.ok) {
    const body = (await response.json().catch(() => null)) as {
      message?: string;
      code?: string;
      requestId?: string;
      errorType?: string;
    } | null;
    throw new ApiError(
      body?.message ?? "Belge önizlemesi açılamadı.",
      body?.code ?? `HTTP_${response.status}`,
      body?.requestId ?? response.headers.get("x-request-id") ?? undefined,
      body?.errorType,
    );
  }
  return response.blob();
}
